// @ts-check
// @ts-ignore - no type declaration for the "three" module; only a global THREE namespace exists.
import THREE from "three";
import { OutOfCoreTaskBase } from "../OutOfCoreTaskBase";
import { MeshFlags } from "../../../MeshFlags";
import { LOD_MAPPING_MODE } from "../../../../../file-loaders/rcs/RcsConstants";
import { getLodRange } from "../../../../../file-loaders/rcs/RcsLodMapping";
import { rcsWorkerPool } from "../../../../../file-loaders/rcs/RcsWorkerPool";

/** @import {BvhNode} from "../../BvhNode" */
/** @import {RcsFileLoader, RcsDecodedPoints, RcsLevelDecodeResult } from "../../../../../file-loaders/rcs/RcsFileLoader" */
/** @import { ParentLodRange, RcsPackage } from "../../../../../file-loaders/rcs/RcsPackage" */
/** @import { Model } from "../../../../../application/Model" */
/** @import { Viewer3DImpl } from "../../../../../application/Viewer3DImpl" */
/** @import {LodRange} from "../../../../../file-loaders/rcs/RcsLodMapping" */
/** @import {UmbrellaLeafNode, RcsExtractorCore} from "../../../../../file-loaders/rcs/RcsExtractorCore" */
/** @import { LeanBufferGeometry } from "../../../BufferGeometry" */

/** Throttle window for RCS load-time invalidation (ms). */
const REDRAW_INTERVAL_MS = 2000;

// Reusable scratch objects for transforming HLOD points into parent space to avoid GC overhead.
const _scratchParentInverse = new THREE.Matrix4();
const _scratchCombinedTransform = new THREE.Matrix4();
const _scratchLeafTransform = new THREE.Matrix4();
const _scratchParentTransform = new THREE.Matrix4();
const _scratchPoint = new THREE.Vector3();

/**
 * Task for loading RCS octree nodes on-demand via the OutOfCoreTileManager.
 * Each task loads all fragments belonging to a single BVH node.
 */
export class LoadRcsNodeTask extends OutOfCoreTaskBase {

  /** @type {number} - Timestamp of last invalidate call for throttling */
  static _lastInvalidateTime = 0;
  /** @type {number|undefined} - Timeout ID for debounced redraw */
  static _redrawTimeout = undefined;

  /**
   * Create a task to load RCS geometry for a BVH node.
   * @param {BvhNode} node - The BVH node to process in this task
   */
  constructor(node) {
    super(node.outOfCoreTileManager);
    /** @type {BvhNode} - The BVH node being processed */
    this.node = node;
    /** @type {Array<number>} - Fragment IDs this task activated */
    this.activatedFragments = [];
  }

  /**
   * Load RCS node data for all fragments in this BVH node.
   * Uses async loading to yield control and respect time budget.
   * @returns {Promise<void>}
   */
  async execute() {
    const model = /** @type {Model} */this.node.model;
    const rcsLoader = /** @type {RcsFileLoader} */model.loader;
    const pkg = /** @type {RcsPackage} */model.getData();
    const nodeId = this.node.nodeId;
    const renderBatch = model.getIterator().getGeomScenes()[nodeId];
    const renderBatchIndices = renderBatch.getIndices();

    // Load geometry for all fragments in this batch
    for (let i = renderBatch.start; i < renderBatch.lastItem; i++) {
      const fragId = renderBatchIndices[i];
      if (fragId === undefined) continue;

      if (pkg.isInnerNode(nodeId)) {
        // Inner node with HLOD points — create empty geometry that will be filled
        // by child nodes' _loadFragmentWithHierarchicalLOD
        const fragmentList = model.getFragmentList();
        const pointCount = pkg.getNodePointCount(nodeId);
        if (pointCount > 0) {
          if (!fragmentList.getGeometry(fragId)) {
            rcsLoader._addAndActivateFragment(fragId, { pointCount });
            fragmentList.setFlagFragment(fragId, MeshFlags.MESH_VISIBLE, true);

            // TODO: we currently never unload the inner nodes (by bumping their screenspace error)
            //       and we don't add them to the activatedFragments array here (so they stay loaded forever).
            //       Unloading them would mean that we have to refetch all umbrella nodes, which is quite expensive.
            //       Once we have support for the OPFS cache, we should allow unloading of cached nodes.
            //       Note: The RCSFileLoader uses the assumption these stay loaded for load-progress reporting.
            //             If this is changed, the load progress needs to be updated.
            //this.activatedFragments.push(fragId);
          }
        } else {
          // If there are no points in the inner node, mark the fragment as off
          fragmentList.setFragOff(fragId, true);
        }
      } else if (pkg.getNodeLodLevel(nodeId) === 0 && pkg.getNodeUmbrellaIndex(nodeId) >= 0) {
        // LOD-0 umbrella leaf: distributes points to parent nodes via hierarchical LOD.
        // The leaf fragment itself stays off — its points live in parent geometries.
        const lodLevel = pkg.getNodeLodLevel(nodeId);
        /** @type {LodRange} */
        const range = getLodRange(lodLevel);
        const startPoint = /** @type {number} */LOD_MAPPING_MODE === 'direct' ? range.startLevel : range.startPoint;
        const endPoint = /** @type {number} */LOD_MAPPING_MODE === 'direct' ? range.endLevel : range.endPoint;

        const umbrellaNodeIndex = pkg.getNodeUmbrellaIndex(nodeId);
        const leafNode = pkg.getUmbrellaLeafNode(umbrellaNodeIndex);

        await this._loadFragmentWithHierarchicalLOD(
          rcsLoader, pkg, fragId, nodeId, umbrellaNodeIndex, leafNode, startPoint, endPoint
        );

        // Bail out if the task was canceled during the await: undo() has already
        // run, so don't start loading further fragments.
        if (this.canceled) return;

        model.getFragmentList().setFragOff(fragId, true);
      } else {
        // Standard LOD fragment — load points directly into this fragment's geometry
        const lodLevel = pkg.getNodeLodLevel(nodeId);
        /** @type {LodRange} */
        const range = getLodRange(lodLevel);
        const startPoint = /** @type {number} */LOD_MAPPING_MODE === 'direct' ? range.startLevel : range.startPoint;
        const endPoint = /** @type {number} */LOD_MAPPING_MODE === 'direct' ? range.endLevel : range.endPoint;

        await this._loadFragmentWithRange(rcsLoader, pkg, fragId, nodeId, startPoint, endPoint);

        // Bail out if the task was canceled during the await: undo() has already
        // run, so don't start loading further fragments.
        if (this.canceled) return;
      }
    }

    // For OTG, redraws are scheduled by the OTGLoader in a different place. For RCS, this
    // invalidation (and the throttled follow-up in _scheduleRedraw) is needed so new
    // geometry appears on screen as it is being loaded.
    this._scheduleRedraw(rcsLoader);

    // Notify the loader that a coarse node finished. We do this here (rather than inside
    // _addAndActivateFragment) so the count also advances for coarse nodes whose load was
    // skipped because the SVO range was empty — those still represent completed work and
    // must contribute to progress, otherwise the indicator would stall at <100%.
    if (pkg.isCoarseNode(nodeId)) {
      rcsLoader.onCoarseNodeLoaded();
    }
  }

  /**
   * Load RCS fragment with a specified point range.
   * Standard loading path: fetches SVO data, decodes it, and creates geometry
   * directly for this fragment.
   * @private
   * @param {RcsFileLoader} rcsLoader - The RCS loader instance
   * @param {RcsPackage} pkg - The RCS package with model data
   * @param {number} fragId - Fragment ID to load
   * @param {number} nodeId - BVH node ID
   * @param {number} startPoint - Start of point range (0-based)
   * @param {number} endPoint - End of point range (inclusive target)
   * @returns {Promise<number>} Actual number of points loaded
   */
  async _loadFragmentWithRange(rcsLoader, pkg, fragId, nodeId, startPoint, endPoint) {
    const umbrellaNodeIndex = pkg.getNodeUmbrellaIndex(nodeId);
    const leafNode = pkg.getUmbrellaLeafNode(umbrellaNodeIndex);

    // Calculate SVO data location and fetch + decode in worker
    const { url, offset, length } = this._calculateSvoDataLocation(
      rcsLoader, pkg, umbrellaNodeIndex, startPoint, endPoint
    );

    // If there's no data to load (fragment doesn't have points in this range), skip.
    // The fragment will remain in MESH_NOTLOADED state for this LOD pass.
    if (length === 0) return 0;

    // Decode SVO payload in worker (worker will fetch the data)
    const localPoints = await this._decodeSvoPayloadInWorker(
      url, offset, length, leafNode, startPoint, endPoint
    );

    // The task may have been canceled (node demoted/freed) while the worker decode
    // was in flight. undo() has already run by then, so we must not activate any new
    // geometry here — doing so would leak a fragment that nothing will deactivate.
    // The synchronous check right after the await is race-free (no interleaving).
    if (this.canceled) return 0;

    if (localPoints.pointCount === 0) return 0;

    // Create THREE.js geometry from interleaved point data and activate the fragment
    const model = /** @type {Model} */rcsLoader.model;
    const fragmentList = model.getFragmentList();
    fragmentList.setFragOff(fragId, false);
    fragmentList.setFlagFragment(fragId, MeshFlags.MESH_SWAPPED_OUT_OF_CORE, false);
    fragmentList.setFlagFragment(fragId, MeshFlags.MESH_NOTLOADED, false);

    const geometry = rcsLoader._addAndActivateFragment(fragId, localPoints);

    // Record the fragment so undo() can deactivate it.
    this.activatedFragments.push(fragId);

    // The resource cost is only estimated. If there are deleted points in the node,
    // we will need less memory than indicated by the umbrella node.
    console.assert(this.getResourceCost() >= geometry.getByteSize());

    return localPoints.pointCount;
  }

  /**
   * Load fragment with hierarchical LOD distribution.
   * Decodes points level-by-level and distributes them to parent BVH nodes
   * for coarse LOD visualisation.
   * @private
   * @param {RcsFileLoader} rcsLoader - The RCS loader instance
   * @param {RcsPackage} pkg - The RCS package with model data
   * @param {number} fragId - Fragment ID to load
   * @param {number} nodeId - BVH node ID
   * @param {number} umbrellaNodeIndex - Index of the umbrella node
   * @param {UmbrellaLeafNode} leafNode - The leaf node metadata
   * @param {number} startPoint - Start of point range (0-based)
   * @param {number} endPoint - End of point range (inclusive target)
   * @returns {Promise<number>} Actual number of points loaded
   */
  async _loadFragmentWithHierarchicalLOD(rcsLoader, pkg, fragId, nodeId, umbrellaNodeIndex, leafNode, startPoint, endPoint) {
    const model = /** @type {Model} */rcsLoader.model;

    // Calculate SVO data location
    const { url, offset, length } = this._calculateSvoDataLocation(
      rcsLoader, pkg, umbrellaNodeIndex, startPoint, endPoint
    );

    // If there's no data to load, skip.
    // The fragment will remain in MESH_NOTLOADED state for this LOD pass.
    if (length === 0) return 0;

    // Decode points by LOD level using the specialized worker function
    const result = await this._decodeSvoPayloadByLevelInWorker(
      url, offset, length, leafNode, startPoint, endPoint
    );

    // The task may have been canceled (node demoted/freed) while the worker decode
    // was in flight. undo() has already run by then, so we must not activate the leaf
    // fragment or distribute points to parent geometries. The synchronous check right
    // after the await is race-free (no interleaving).
    if (this.canceled) return 0;

    if (!result.pointsByLevel || result.pointsByLevel.length === 0) {
      // There can be leaf nodes with no points in an RCS file, if all points are deleted.
      return 0;
    }

    // Get the parent LOD ranges that define where each level's points go
    const parentLodRanges = pkg.getNodeParentLodRanges(nodeId);
    if (!parentLodRanges) {
      console.warn(`[H-LOD] No parent LOD ranges found for node ${nodeId} (umbrella ${umbrellaNodeIndex})`);
      return 0;
    }

    const pointCount = pkg.getNodePointCount(nodeId);
    const leafGeometry = rcsLoader._addAndActivateFragment(fragId, { pointCount });
    model.getFragmentList().setFlagFragment(fragId, MeshFlags.MESH_VISIBLE, true);

    // Record the leaf now that it's actually activated, so undo() deactivates exactly
    // the fragments this task brought in. Note this captures activation even when the
    // method later returns 0 points (e.g. all level data distributed to parents).
    this.activatedFragments.push(fragId);

    // The resource cost is only estimated. If there are deleted points in the node,
    // we will need less memory than indicated by the umbrella node.
    console.assert(pkg.computeNodeMemorySize(nodeId) >= leafGeometry.getByteSize());

    // Create leaf transform matrix from SVO decode result.
    // Fragment matrices are in viewer coordinates (world minus globalOffset),
    // so the leaf origin must use the same space when distributing to parents.
    const globalOffset = pkg.globalOffset;
    _scratchLeafTransform.makeTranslation(
      result.transformData.offsetX - globalOffset.x,
      result.transformData.offsetY - globalOffset.y,
      result.transformData.offsetZ - globalOffset.z
    );

    const bvh = model.getIterator().getBVH();

    // Distribute each level's points to the appropriate parent node
    for (const levelData of result.pointsByLevel) {
      const { interleavedData: points, count } = levelData;
      if (!points || count === 0) continue; // Skip levels with no points

      // Find the parent LOD range for this level
      const parentRange = parentLodRanges.find((r) => r.level === levelData.level);
      if (!parentRange) continue;

      if (!pkg.isInnerNodeWithPoints(parentRange.bvhNodeId)) {
        continue;
      }

      // Get the parent node's geometry
      const fragmentList = model.getFragmentList();
      const parentFragId = bvh.nodes.getPrimStart(parentRange.bvhNodeId);
      const parentGeometry = fragmentList.getGeometry(parentFragId);
      if (!parentGeometry) {
        console.warn(`No geometry found for inner fragment ${parentFragId}`);
        continue;
      }

      // Use the fragment's stored translation, not the live BVH box: ModelIteratorBVH's
      // #updateBVHRec widens BVH boxes to the union of children's render-batch bounds,
      // so by the time HLOD writes points the box no longer matches the fragment's position.
      fragmentList.getOriginalWorldMatrix(parentFragId, _scratchParentTransform);

      // Transform points from leaf to parent coordinate system and write them
      // directly into the parent geometry's reserved range (no temporary buffer).
      this._transformAndWritePointsToGeometry(
        parentGeometry, points, count, _scratchLeafTransform, _scratchParentTransform,
        parentRange.rangeStart, parentRange.rangeEnd
      );

      // Update GPU buffer if geometry has already been uploaded
      this._uploadGeometryIfNeeded(rcsLoader, parentGeometry, parentRange);

      // If the fragment is still marked as off, enable it now that it has points
      if (fragmentList.isFragOff(parentFragId)) {
        fragmentList.setFragOff(parentFragId, false);
        fragmentList.setFlagFragment(parentFragId, MeshFlags.MESH_SWAPPED_OUT_OF_CORE, false);
      }
    }

    // Mark the low-LOD fragment as deactivated if it does not contain any points itself
    if (pkg.getNodePointCount(nodeId) === 0) {
      const fragmentList = model.getFragmentList();
      fragmentList.setFragOff(fragId, true);
      fragmentList.setFlagFragment(fragId, MeshFlags.MESH_SWAPPED_OUT_OF_CORE, false);
    }

    // Return the actual number of points loaded (sum across all levels)
    return result.pointsByLevel.reduce((sum, level) => sum + level.count, 0);
  }

  /**
   * Schedule a throttled redraw after loading.
   * @private
   * @param {RcsFileLoader} rcsLoader
   */
  _scheduleRedraw(rcsLoader) {
    const viewerImpl = /** @type {Viewer3DImpl} */rcsLoader.viewer3DImpl;
    if (performance.now() - LoadRcsNodeTask._lastInvalidateTime > REDRAW_INTERVAL_MS) {
      viewerImpl.invalidate(false, true);
      LoadRcsNodeTask._lastInvalidateTime = performance.now();
    }

    if (LoadRcsNodeTask._redrawTimeout) {
      clearTimeout(LoadRcsNodeTask._redrawTimeout);
      LoadRcsNodeTask._redrawTimeout = undefined;
    }
    LoadRcsNodeTask._redrawTimeout = setTimeout(() => {
      viewerImpl.invalidate(false, true);
      LoadRcsNodeTask._lastInvalidateTime = performance.now();
    }, REDRAW_INTERVAL_MS);
  }

  /**
   * Upload geometry sub-range to GPU if already uploaded (non-streaming).
   * If geometry.streamingDraw is true, the CPU-side buffer (geometry.vb)
   * will be automatically uploaded on the next draw call — no action needed.
   * @private
   * @param {RcsFileLoader} rcsLoader
   * @param {THREE.BufferGeometry} geometry
   * @param {ParentLodRange} range
   */
  _uploadGeometryIfNeeded(rcsLoader, geometry, range) {
    // streamingDraw is tri-state: false, true, or the STREAMING_DRAW_ONCE_DURING_UPLOAD
    // If true, geometry is drawn from the CPU side buffer and there is nothing to do.
    // If STREAMING_DRAW_ONCE_DURING_UPLOAD (WebGL only) a single frame of streaming draw
    // is performed before swapping to a persistent GPU buffer which needs to be updated here.
    if (geometry.streamingDraw === true) return;

    const renderer = /** @type {Viewer3DImpl} */rcsLoader.viewer3DImpl._webglrender;
    if (!renderer) return;

    if (geometry.ib) {
      // WebGL relies on an index buffer.
      const alignmentSize = 4 / geometry.ib.BYTES_PER_ELEMENT;
      renderer.uploadGeometry(geometry, {
        vertexStart: range.rangeStart,
        vertexEnd: range.rangeEnd,
        indexStart: Math.floor(range.rangeStart / alignmentSize) * alignmentSize,
        indexEnd: Math.ceil(range.rangeEnd / alignmentSize) * alignmentSize
      });
    } else {
      // WebGPU doesn't use an index buffer.
      renderer.uploadGeometry(geometry, {
        vertexStart: range.rangeStart,
        vertexEnd: range.rangeEnd
      });
    }
  }

  /**
   * Transform points from leaf coordinate system into parent coordinate system and
   * write them directly into the parent geometry's reserved range.
   *
   * @private
   * @param {LeanBufferGeometry} geometry - Target geometry
   * @param {Float32Array} points - Interleaved point data (XYZRGB)
   * @param {number} count - Number of points
   * @param {THREE.Matrix4} leafTransform - Leaf node transform matrix
   * @param {THREE.Matrix4} parentTransform - Parent node transform matrix
   * @param {number} rangeStart - Start index in the geometry's vertex buffer
   * @param {number} rangeEnd - End index (exclusive) in the geometry's vertex buffer
   */
  _transformAndWritePointsToGeometry(geometry, points, count, leafTransform, parentTransform, rangeStart, rangeEnd) {
    // Get the interleaved vertex buffer (LMV format)
    const buffer = geometry.vb;
    if (!buffer) {
      console.error('Geometry missing interleaved vertex buffer (vb)');
      return;
    }

    // Verify we have enough space (allow some flexibility for deleted points)
    const rangeSize = rangeEnd - rangeStart;
    const actualCount = Math.min(count, rangeSize);

    if (count > rangeSize) {
      console.warn(`Point count ${count} exceeds allocated range size ${rangeSize}, using ${actualCount} points`);
    }

    // Combined transformation matrix: parent^-1 * leaf.
    // This transforms from leaf local space directly to parent local space.
    _scratchParentInverse.copy(parentTransform).invert();
    _scratchCombinedTransform.multiplyMatrices(_scratchParentInverse, leafTransform);

    // Transform each point and write it straight into the reserved range.
    // points/buffer format: [x, y, z, r, g, b, ...] with position at offset 0 and color
    // at offset 3 within each stride (see RcsFileLoader._createPointGeometry's vblayout).
    // Source decode and destination vb share the same stride, so a single value drives both.
    const stride = geometry.vbstride ?? 6;
    const destOffset = rangeStart * stride;
    for (let i = 0; i < actualCount; i++) {
      const srcIdx = i * stride;
      const dstIdx = destOffset + srcIdx;

      // Transform position
      _scratchPoint.set(points[srcIdx], points[srcIdx + 1], points[srcIdx + 2]);
      _scratchPoint.applyMatrix4(_scratchCombinedTransform);

      buffer[dstIdx] = _scratchPoint.x;
      buffer[dstIdx + 1] = _scratchPoint.y;
      buffer[dstIdx + 2] = _scratchPoint.z;

      // Copy color unchanged
      buffer[dstIdx + 3] = points[srcIdx + 3];
      buffer[dstIdx + 4] = points[srcIdx + 4];
      buffer[dstIdx + 5] = points[srcIdx + 5];
    }

    //  Note: vbNeedsUpdate mustn't be set here as it signals "full re-upload pending" not partial updates.
    // @ts-ignore do we need this?
    geometry.needsUpdate = true;
  }

  /**
   * Calculate the URL, byte offset, and byte length for SVO data.
   * This replicates the logic from RcsExtractorCore.readRawSvoPayload.
   * @private
   * @param {RcsFileLoader} rcsLoader - The RCS loader instance
   * @param {RcsPackage} pkg - The RCS package with model data
   * @param {number} umbrellaNodeIndex - Index of the umbrella node
   * @param {number} startPoint - Start of point range (0-based)
   * @param {number} endPoint - End of point range (inclusive target)
   * @returns {{url: string|null, offset: number, length: number, actualEndPoint: number|undefined}}
   */
  _calculateSvoDataLocation(rcsLoader, pkg, umbrellaNodeIndex, startPoint, endPoint) {
    const extractor = /** @type {RcsExtractorCore} */rcsLoader.rcsExtractor;
    const leafNode = pkg.getUmbrellaLeafNode(umbrellaNodeIndex);

    // Calculate required data size based on LOD range
    const { offset: dataOffset, size, points: actualEndPoint } = extractor._calculateRequiredDataSize(leafNode, startPoint, endPoint);

    // Get voxel chunk info
    const voxelChunk = extractor.chunks.find((c) => c.chunkId === 1);
    if (!voxelChunk) {
      throw new Error('Voxel data chunk (ID=1) not found');
    }

    // Cumulative offset for this leaf. The per-leaf prefix sum is precomputed in
    // RcsPackage, so this is O(1) rather than re-scanning all preceding leaves.
    const leafDataOffset = Number(voxelChunk.fileOffsetStart) + pkg.getUmbrellaLeafDataOffset(umbrellaNodeIndex);

    // Convert relative URL to absolute URL for worker fetch().
    // Workers require absolute URLs for fetch().
    let absoluteUrl = extractor.source;
    if (absoluteUrl && typeof window !== 'undefined' && !this._isAbsoluteUrl(absoluteUrl)) {
      absoluteUrl = new URL(absoluteUrl, window.location.href).href;
    }

    return {
      url: absoluteUrl,
      offset: leafDataOffset + dataOffset,
      length: size,
      actualEndPoint
    };
  }

  /**
   * Check if a URL is absolute (has a protocol like http://, https://, file://).
   * @private
   * @param {string} url - URL to check
   * @returns {boolean} True if URL is absolute
   */
  _isAbsoluteUrl(url) {
    return /^[a-z][a-z0-9+.-]*:/i.test(url);
  }

  // ---- Static worker infrastructure ----

  // ---- Request dispatch ----

  /**
   * Send a fetch-and-decode request to the dedicated fetch worker.
   * Returns a promise that resolves when the decode worker posts the result.
   * @private
   * @param {string} decodeOp - Worker operation name ('RCS_DECODE' | 'RCS_DECODE_BY_LEVEL')
   * @param {string|null} url - Absolute URL to fetch SVO data from
   * @param {number} offset - Byte offset in the file
   * @param {number} length - Number of bytes to read
   * @param {UmbrellaLeafNode} leafNode - The leaf node metadata
   * @param {number} startPoint - Start of point range (0-based)
   * @param {number} endPoint - End of point range (inclusive target)
   * @returns {Promise<any>}
   */
  _sendFetchAndDecode(decodeOp, url, offset, length, leafNode, startPoint, endPoint) {
    return rcsWorkerPool.dispatchFetchAndDecode({
      decodeOp,
      url,
      offset,
      length,
      urn: /** @type {RcsFileLoader} */this.node.model.loader.urn, // Forwarded to RcsWebSocketBatcher for WebSocket auth
      token: /** @type {RcsFileLoader} */this.node.model.loader.token, // Same
      leafNode: {
        svoBounds: leafNode.svoBounds,
        numNonLeafNodes: leafNode.numNonLeafNodes,
        numSvoLeafNodes: leafNode.numSvoLeafNodes
      },
      startPoint,
      endPoint,
      interiorNodesIncluded: false
    });

  }

  /**
   * Fetch + decode SVO payload via workers (single-phase from main-thread perspective).
   * @private
   * @param {string|null} url - Absolute URL to fetch SVO data from
   * @param {number} offset - Byte offset in the file
   * @param {number} length - Number of bytes to read
   * @param {UmbrellaLeafNode} leafNode - The leaf node metadata
   * @param {number} startPoint - Start of point range (0-based)
   * @param {number} endPoint - End of point range (inclusive target)
   * @returns {Promise<RcsDecodedPoints>}
   */
  async _decodeSvoPayloadInWorker(url, offset, length, leafNode, startPoint, endPoint) {
    return this._sendFetchAndDecode('RCS_DECODE', url, offset, length, leafNode, startPoint, endPoint);
  }

  /**
   * Fetch + decode-by-level SVO payload via workers.
   * @private
   * @param {string|null} url - Absolute URL to fetch SVO data from
   * @param {number} offset - Byte offset in the file
   * @param {number} length - Number of bytes to read
   * @param {UmbrellaLeafNode} leafNode - The leaf node metadata
   * @param {number} startPoint - Start of point range (0-based)
   * @param {number} endPoint - End of point range (inclusive target)
   * @returns {Promise<RcsLevelDecodeResult>}
   */
  async _decodeSvoPayloadByLevelInWorker(url, offset, length, leafNode, startPoint, endPoint) {
    return this._sendFetchAndDecode('RCS_DECODE_BY_LEVEL', url, offset, length, leafNode, startPoint, endPoint);
  }

  /**
   * Calculate the memory cost of loading this BVH node.
   * Returns the total memory required for all fragments in this node.
   * @returns {number} Total memory in bytes
   */
  getResourceCost() {
    const model = /** @type {Model} */this.node.model;
    const pkg = /** @type {RcsPackage} */model.getData();
    return pkg.computeNodeMemorySize(this.node.nodeId);
  }

  /**
   * Cleanup loaded fragments when node is demoted.
   */
  undo() {
    for (const fragId of this.activatedFragments) {
      const rcsLoader = /** @type {RcsFileLoader} */this.node.model.loader;
      rcsLoader._deactivateFragment(fragId);
    }

    this.activatedFragments = [];
  }
}